import React from 'react';
import { CHAT_PENDING_TIMEOUT_MS } from '../constants';
import { useSendbirdConnection } from '../hooks';
import { useIsConnectionPending } from '../hooks/useIsConnectionPending';
import { useLoadTimeout } from '../hooks/useLoadTimeout';
import { ConnectionStatus } from './ConnectionStatus';
import { ChatConnectingState } from './ChatConnectingState';
import { ChatConnectionFallback } from './ChatConnectionFallback';
import { IWithSendbirdConnectionProps } from '../types';

export const withSendbirdConnection = <P extends object>(
    WrappedComponent: React.ComponentType<P>,
    options: IWithSendbirdConnectionProps = {}
) => {
    const {
        useDetailedConnectionStatus = true,
        ConnectingComponent = ChatConnectingState,
    } = options;
    return (props: P) => {
        const connectionState = useSendbirdConnection();
        const { isConnecting, isConnected, connectionError, refreshToken } = connectionState;
        const isPending = useIsConnectionPending(connectionState);
        const pendingExpired = useLoadTimeout(CHAT_PENDING_TIMEOUT_MS);

        if (isConnecting || (isPending && !pendingExpired)) {
            return <ConnectingComponent />;
        }

        if (!isConnected || connectionError) {
            if (useDetailedConnectionStatus) {
                return (
                    <ConnectionStatus
                        isConnecting={isConnecting}
                        isConnected={isConnected}
                        connectionError={connectionError}
                        onReconnect={refreshToken}
                    />
                );
            } else {
                return <ChatConnectionFallback />;
            }
        }

        return (
            <WrappedComponent
                {...props}
                sendbirdConnection={connectionState}
            />
        );
    };
};
